// Derives what the Track page shows from an application record.
//
// Always present from the backend: company-name, position-name, application-id, status,
// interview-time, interview-accepted.
// Optional, and hidden when missing:
//   company-id         used to load the company logo
//   reference          human-readable reference, e.g. "CFAS-2027-000118"
//   updated-at         epoch seconds of the last change
//   location           interview location, e.g. "SACC, booth B4"
//   history            [{stage, date}] where stage is SUBMITTED, VIEWED, SHORTLISTED, INTERVIEW, ACCEPTED or UNSUCCESSFUL
//                      and date is epoch seconds
//   company-form-url   the company's own application form, with company-form-done once completed
//   message, message-date   the latest message from the company

// The status shown to the student, which splits INTERVIEW by whether an interview time has been set and accepted.
export function getDisplayStatus(application) {
    const status = (application["status"] || "SUBMITTED").toUpperCase();

    if (status !== "INTERVIEW") {
        return status
    }
    if (!application["interview-time"]) {
        return "INTERVIEW"
    }
    return application["interview-accepted"] === true ? "INTERVIEW CONFIRMED" : "ACTION REQUIRED - INTERVIEW"
}

export function formatDate(epochSeconds) {
    if (!epochSeconds) {
        return "";
    }
    const date = new Date(epochSeconds * 1000);
    const options = {day: "numeric", month: "short"};
    if (date.getFullYear() !== new Date().getFullYear()) {
        options.year = "numeric";
    }
    return date.toLocaleDateString("en-GB", options);
}

function history(application) {
    return Array.isArray(application["history"]) ? application["history"].filter((entry) => entry && entry.stage) : [];
}

function historyDate(application, stage) {
    const entry = history(application).filter((e) => e.stage === stage).pop();
    return entry ? entry.date : undefined;
}

function hasFormToComplete(application) {
    return Boolean(application["company-form-url"]) && !application["company-form-done"];
}

const statusChips = {
    "ACTION REQUIRED - INTERVIEW": {label: "Action needed", tone: "amber"},
    "INTERVIEW CONFIRMED": {label: "Interview", tone: "green"},
    "INTERVIEW": {label: "Interview", tone: "green"},
    "ACCEPTED": {label: "Offer", tone: "green"},
    "UNSUCCESSFUL": {label: "Not selected", tone: "red"},
    "WITHDRAWN": {label: "Withdrawn", tone: "grey"},
};

// The furthest of Submitted, Viewed or Shortlisted reached, for applications still waiting on the company.
function reviewStage(application) {
    const stages = history(application).map((entry) => entry.stage);
    if (stages.includes("SHORTLISTED")) {
        return "SHORTLISTED";
    }
    if (stages.includes("VIEWED")) {
        return "VIEWED";
    }
    return "SUBMITTED";
}

export function statusChip(application) {
    const status = getDisplayStatus(application);
    if (statusChips[status]) {
        return statusChips[status];
    }
    if (status === "SUBMITTED") {
        return {
            SUBMITTED: {label: "Submitted", tone: "grey"},
            VIEWED: {label: "Viewed", tone: "grey"},
            SHORTLISTED: {label: "Shortlisted", tone: "green"},
        }[reviewStage(application)];
    }
    return {label: status.charAt(0) + status.slice(1).toLowerCase(), tone: "grey"};
}

export function isInProgress(application) {
    return !["ACCEPTED", "UNSUCCESSFUL", "WITHDRAWN"].includes(getDisplayStatus(application));
}

// Students can withdraw only while the application is submitted and the company hasn't looked at it yet.
export function canWithdraw(application) {
    return getDisplayStatus(application) === "SUBMITTED" && reviewStage(application) === "SUBMITTED";
}

function interviewPlace(application) {
    return application["location"] ? ` at ${application["location"]}` : "";
}

// The next step text and button for an application. needsYou marks steps the student has to act on.
export function nextStep(application) {
    const status = getDisplayStatus(application);
    const company = application["company-name"] || "The company";

    if (status === "ACTION REQUIRED - INTERVIEW") {
        return {
            text: "Accept your interview invitation within 24 hours.",
            action: {label: "Respond", opensDetails: true, primary: true},
            needsYou: true
        };
    }
    if (hasFormToComplete(application) && isInProgress(application)) {
        return {
            text: `Complete ${company}'s own form`,
            action: {label: "Open form", href: application["company-form-url"], primary: true},
            needsYou: true
        };
    }
    if (status === "INTERVIEW CONFIRMED") {
        return {
            text: `Interview${interviewPlace(application)}, ${application["interview-time"]}.`,
            action: {label: "See My SACC", to: "/cfas/event/home"}
        };
    }
    if (status === "INTERVIEW") {
        return {
            text: `Interview${interviewPlace(application)}. Time comes by email.`,
            action: {label: "See My SACC", to: "/cfas/event/home"}
        };
    }
    if (status === "ACCEPTED") {
        return {
            text: `${company} has offered you a place. They will contact you directly.`,
            action: {label: "View details", opensDetails: true}
        };
    }
    if (status === "WITHDRAWN") {
        return {
            text: "You withdrew this application.",
            action: {label: "Find roles", to: "/cfas/apply/home"}
        };
    }
    if (status === "UNSUCCESSFUL") {
        return {
            text: "Not selected this time. You can still apply for other roles.",
            action: {label: "Find roles", to: "/cfas/apply/home"}
        };
    }
    return {
        text: `Waiting for ${company} to review your application.`,
        action: null
    };
}

// One line under the status chip, e.g. "Updated 9 Oct by Bangkok Bank".
export function lastUpdate(application) {
    const entries = history(application);
    const latest = entries.length > 0 ? entries.reduce((a, b) => ((b.date || 0) >= (a.date || 0) ? b : a)) : undefined;
    const date = application["updated-at"] || (latest && latest.date) || application["message-date"];
    if (!date) {
        return "";
    }
    const byCompany = latest ? latest.stage !== "SUBMITTED" : Boolean(application["message-date"]);
    return `Updated ${formatDate(date)}${byCompany && application["company-name"] ? ` by ${application["company-name"]}` : ""}`;
}

// A short explanation shown next to the timeline.
export function stageSummary(application) {
    return {
        "ACTION REQUIRED - INTERVIEW": "You've been invited to an interview. Accept the invitation to keep your slot.",
        "INTERVIEW CONFIRMED": "Interview confirmed. Prepare, and arrive on time.",
        "INTERVIEW": "Interview arranged. Prepare, and check your email for the time.",
        "ACCEPTED": "Congratulations! The company will contact you directly about your offer.",
        "UNSUCCESSFUL": "The company has decided not to go ahead with this application.",
        "WITHDRAWN": "You withdrew this application, so the company can no longer see it.",
    }[getDisplayStatus(application)] || "Your application is with the company. You'll get an email when anything changes.";
}

// Timeline steps, each with state "done", "current" or "upcoming".
// Viewed and Shortlisted are only shown when the backend sends a history to fill them from.
export function timeline(application) {
    const status = getDisplayStatus(application);
    const company = application["company-name"] || "The company";
    const withHistory = history(application).length > 0;

    let reached;
    if (status === "WITHDRAWN") {
        reached = "SUBMITTED";
    } else if (status === "ACCEPTED" || status === "UNSUCCESSFUL") {
        reached = "OUTCOME";
    } else if (status.includes("INTERVIEW")) {
        reached = "INTERVIEW";
    } else {
        reached = reviewStage(application);
    }

    const interviewDetail = application["location"]
        ? `At ${application["location"]}`
        : (application["interview-time"] || "Time to be confirmed");

    const steps = [
        {key: "SUBMITTED", label: "Submitted", detail: formatDate(historyDate(application, "SUBMITTED"))},
        {key: "VIEWED", label: "Viewed", detail: `${company} opened your application`, optional: true},
        {key: "SHORTLISTED", label: "Shortlisted", detail: `${company} wants to talk to you`, optional: true},
        {key: "INTERVIEW", label: "Interview", detail: interviewDetail},
        {
            key: "OUTCOME",
            label: "Outcome",
            detail: {ACCEPTED: "Offer", UNSUCCESSFUL: "Not selected", WITHDRAWN: "Withdrawn"}[status] || "Offer or not selected"
        },
    ].filter((step) => withHistory || !step.optional);

    const reachedIndex = steps.findIndex((step) => step.key === reached);

    return steps.map((step, index) => {
        let state = "upcoming";
        if (index < reachedIndex || (index === reachedIndex && step.key !== "INTERVIEW")) {
            state = "done";
        } else if (index === reachedIndex) {
            state = "current";
        }
        // An interview that never happened (rejected straight after review) is shown as skipped.
        if (step.key === "INTERVIEW" && state === "done" && status === "UNSUCCESSFUL" && !historyDate(application, "INTERVIEW")) {
            return {...step, state: "upcoming", detail: "Not reached"};
        }
        return {...step, state, detail: state === "upcoming" && step.key !== "OUTCOME" ? "" : step.detail};
    });
}

const updateText = {
    SUBMITTED: "Application sent.",
    VIEWED: "opened your application.",
    SHORTLISTED: "shortlisted you.",
    ACCEPTED: "offered you a place.",
    UNSUCCESSFUL: "decided not to go ahead.",
};

// Every dated event across all applications, newest first.
export function updatesFeed(applications) {
    const events = [];

    applications.forEach((application) => {
        history(application).forEach((entry, index) => {
            if (!entry.date) {
                return;
            }
            let text = updateText[entry.stage];
            if (entry.stage === "INTERVIEW") {
                text = `invited you to interview${interviewPlace(application)}.`;
            }
            if (entry.stage === "SUBMITTED" && hasFormToComplete(application)) {
                text += " Complete the company form too.";
            }
            if (text) {
                events.push({key: `${application["application-id"]}-${index}`, date: entry.date, company: application["company-name"], text});
            }
        });

        if (application["message"] && application["message-date"]) {
            events.push({
                key: `${application["application-id"]}-message`,
                date: application["message-date"],
                company: application["company-name"],
                text: "sent you a message."
            });
        }
    });

    return events.sort((a, b) => b.date - a.date);
}
